import React, { useEffect, useState } from 'react';
import { ArrowDown, ChevronRight, PhoneCall } from 'lucide-react';
import { useParallax, useScrollAnimation } from '../utils/useScrollAnimations';
import HeroBanner from './HeroBanner';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [lightTrail, setLightTrail] = useState([]);
  const [videoScale, setVideoScale] = useState(1.5);
  const [videoError, setVideoError] = useState(false);
  const parallaxY = useParallax(0.3);
  const { scrollY } = useScrollAnimation();

  useEffect(() => {
    setIsVisible(true);
    
    // Funkce pro výpočet optimálního video scale
    const calculateVideoScale = () => {
      const windowAspectRatio = window.innerWidth / window.innerHeight;
      const videoAspectRatio = 16 / 9; // YouTube video aspect ratio
      
      // Pro mobilní zařízení (portrait orientace)
      if (windowAspectRatio < 1) {
        // Video je širší než obrazovka, potřebujeme zvětšit více
        setVideoScale(Math.max(2.0, 1 / windowAspectRatio * 1.2));
      } 
      // Pro landscape nebo desktop
      else if (windowAspectRatio < videoAspectRatio) {
        // Obrazovka je užší než video
        setVideoScale(Math.max(1.3, videoAspectRatio / windowAspectRatio * 1.1));
      } 
      else {
        // Obrazovka je širší než video
        setVideoScale(Math.max(1.3, windowAspectRatio / videoAspectRatio * 1.1));
      }
    };
    
    const handleMouseMove = (e) => {
      const newPosition = { x: e.clientX, y: e.clientY };
      setMousePosition(newPosition);
      
      // Přidej novou pozici do trail array
      setLightTrail(prev => {
        const newTrail = [...prev, { ...newPosition, id: Date.now() }];
        // Omez trail na posledních 8 pozic
        return newTrail.slice(-8);
      });
    };

    const handleResize = () => {
      calculateVideoScale();
    };

    // Inicializace
    calculateVideoScale();

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section 
      id="hero"
      className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-white via-red-50 to-gray-50 scroll-optimized"
    >
      {/* Simplified mouse effect - only show when not scrolling much */}
      {scrollY < 200 && lightTrail.slice(0, 3).map((position, index) => (
        <div 
          key={position.id}
          className="fixed pointer-events-none z-40"
          style={{
            left: position.x - 100,
            top: position.y - 100,
            width: `${200 - index * 50}px`,
            height: `${200 - index * 50}px`,
            background: `radial-gradient(circle, rgba(220, 38, 38, ${0.08 - index * 0.02}) 0%, transparent 70%)`,
            borderRadius: '50%',
            filter: 'blur(20px)',
            opacity: 0.6 - (index * 0.2)
          }}
        ></div>
      ))}
      
      {/* Main Mouse Light Effect with Parallax */}
      <div 
        className="fixed pointer-events-none z-50"
        style={{
          left: mousePosition.x - 200,
          top: mousePosition.y - 200,
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(220, 38, 38, 0.15) 0%, rgba(220, 38, 38, 0.08) 30%, rgba(220, 38, 38, 0.03) 60%, transparent 100%)',
          borderRadius: '50%',
          filter: 'blur(30px)',
          transition: 'all 0.2s ease-out',
          opacity: 0.15 * (1 - scrollY * 0.001),
          transform: `translate(${parallaxY * 0.1}px, ${parallaxY * 0.05}px) scale(${1 - scrollY * 0.0003})`
        }}
      ></div>
      
      {/* Pozadí - nejdříve zkusí lokální video, pak fallback */}
      <div 
        className="absolute inset-0 opacity-15"
        style={{
          transform: `translateY(${parallaxY}px) scale(${1 + scrollY * 0.0002})`
        }}
      >
        {!videoError ? (
          <video
            className="absolute inset-0 w-full h-full object-cover filter blur-md"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onError={() => setVideoError(true)}
            style={{
              pointerEvents: 'none',
              transform: `scale(${videoScale})`,
              transformOrigin: 'center center',
              minWidth: `${100 * videoScale}vw`,
              minHeight: `${100 * videoScale}vh`,
              filter: `blur(${6 + scrollY * 0.01}px) brightness(${0.7 - scrollY * 0.0005})`,
              willChange: 'transform, filter'
            }}
          >
            <source src="/videos/background-video.mp4" type="video/mp4" />
            <source src="/videos/background-video.webm" type="video/webm" />
          </video>
        ) : (
          /* Fallback animovaný gradient když video není dostupné */
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 25%, #991b1b 50%, #7f1d1d 75%, #450a0a 100%)',
              backgroundSize: '400% 400%',
              animation: 'gradientShift 8s ease-in-out infinite',
              filter: `blur(${6 + scrollY * 0.01}px) brightness(${0.7 - scrollY * 0.0005})`,
              transform: `scale(${videoScale})`,
              transformOrigin: 'center center'
            }}
          ></div>
        )}
      </div>
      
      {/* Animated background patterns with enhanced movement */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          transform: `translateY(${parallaxY * 0.2}px) rotate(${scrollY * 0.01}deg)`
        }}
      >
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-600 rounded-full filter blur-3xl animate-pulse"
          style={{
            transform: `translate(${Math.sin(scrollY * 0.002) * 20}px, ${Math.cos(scrollY * 0.001) * 30}px) scale(${1 + scrollY * 0.0001})`
          }}
        ></div>
        <div 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-3xl animate-pulse" 
          style={{
            animationDelay: '2s',
            transform: `translate(${Math.cos(scrollY * 0.002) * -25}px, ${Math.sin(scrollY * 0.001) * 40}px) scale(${1 + scrollY * 0.0001})`
          }}
        ></div>
      </div>

      <div 
        className="container mx-auto px-6 text-center relative z-10"
        style={{
          transform: `translateY(${parallaxY * -0.1}px) scale(${1 - scrollY * 0.0002})`
        }}
      >
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Logo ve středu s micro animacemi */}
          <div 
            className="mb-6 flex justify-center"
            style={{
              transform: `translateY(${Math.sin(scrollY * 0.003) * 2}px) rotate(${scrollY * 0.005}deg)`
            }}
          >
            <img 
              src="/vyjedname-logo.png"
              alt="Vyjednáme Logo"
              className="h-20 md:h-24 w-auto transition-transform duration-300 hover:scale-110"
              style={{
                filter: `drop-shadow(0 0 ${10 + Math.sin(scrollY * 0.01) * 5}px rgba(220, 38, 38, 0.3))`
              }}
            />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
            <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Vyjednáme
            </span>
            <br />
            <span className="text-xl md:text-2xl font-normal text-gray-700">
              Váš partner pro IT & Telco
            </span>
          </h1>
          
          <p className="text-lg md:text-xl mb-6 max-w-3xl mx-auto text-gray-700">
            Zařídíme Vám komplexní telco a ICT služby s osobním přístupem a kvalitou.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            {/* Primary CTA */}
            <button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto min-w-[220px] inline-flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-7 py-3.5 rounded-lg font-semibold text-base transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red-500/30 hover:shadow-red-500/50"
            >
              <PhoneCall size={18} />
              Získat nabídku zdarma
            </button>
            
            {/* Secondary CTA – outline */}
            <button 
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto min-w-[220px] inline-flex items-center justify-center gap-2 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-7 py-3.5 rounded-lg font-semibold text-base transition-all duration-300 transform hover:scale-105 bg-white/60 backdrop-blur-sm"
            >
              Služby, které zřizujeme
              <ChevronRight size={18} />
            </button>
          </div>

          <HeroBanner />

        </div>
      </div>

      {/* Scroll hint arrows */}
      <div className="md:hidden absolute bottom-36 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce opacity-60">
        <ArrowDown size={20} className="text-red-500" />
        <ArrowDown size={16} className="text-red-400" />
      </div>
      <div className="hidden md:flex absolute bottom-9 left-1/4 -translate-x-1/2 flex-col items-center gap-1 animate-bounce opacity-60">
        <ArrowDown size={22} className="text-red-500" />
        <ArrowDown size={18} className="text-red-400" />
      </div>
      <div className="hidden md:flex absolute bottom-9 left-3/4 -translate-x-1/2 flex-col items-center gap-1 animate-bounce opacity-60">
        <ArrowDown size={22} className="text-red-500" />
        <ArrowDown size={18} className="text-red-400" />
      </div>
    </section>
  );
};

export default Hero;
